import React, { useState } from 'react';
import { Button } from '@raptormaps/button';
import { Text } from '@raptormaps/text';
import { Row, Stack } from '@raptormaps/layout';
import { Icon } from '@raptormaps/icon';
import { SolarFarm } from '../../api/schemas/SolarFarmSchema';
import { formatDate } from '../../utils/general_utils';
import { FarmInfoCard } from './FarmInfoCard';
import { PartnerCard } from '../FarmDetails/PartnerCard';
import {
  Badge,
  CapacityCard,
  CapacityNumber,
  CapacityUnit,
  CapacityValue,
  CardRow,
  CopiedText,
  CoordsKey,
  CoordsTable,
  CoordsVal,
  CopyBtn,
  Efficiency,
  EfficiencyLabel,
  EfficiencyPct,
  EfficiencyRow,
  FarmGrid,
  IdTop,
  IdentificationCard,
  LoadingContainer,
  LocationCard,
  LocationFields,
  LocationFieldsRow,
  LocationInner,
  LocationRow,
  MapLink,
  MapLinkSub,
  MapNoCoords,
  MapPlaceholder,
  ModuleIcon,
  ModuleTile,
  ModulesRow,
  ModuleVal,
  OrgField,
  PageHeader,
  PageHeaderRight,
  ProgressFill,
  ProgressTrack,
  SpecBig,
  SpecUnit,
  SpecValue,
  ThreeRow,
  Timeline,
  TimelineContent,
  TimelineDate,
  TimelineDot,
  TimelineItem,
  TimelineNote,
  UuidBox,
  UuidText,
  Wrapper,
} from './FarmDetailCard.styles';

export function FarmDetailCard({
  farm,
  loading,
  onEditClick,
}: {
  farm: SolarFarm;
  loading: boolean;
  onEditClick: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyUuid = () => {
    if (farm.uuid) {
      navigator.clipboard.writeText(farm.uuid);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const statusLabel =
    farm.site_status ?? (farm.status !== null ? String(farm.status) : null);

  return (
    <Wrapper>
      <PageHeader>
        <Stack gutter="xxs">
          <Text variant="label_small_allcaps" color="#718096">
            FARMS › DETAILS
          </Text>
          <Row gutter="sm" align="center">
            <Text variant="h3" as="h1">
              {farm.name ?? 'Unnamed Farm'}
            </Text>
            {statusLabel && (
              <Badge
                $variant={
                  statusLabel.toLowerCase() === 'commissioned'
                    ? 'green'
                    : 'blue'
                }
              >
                {statusLabel.toUpperCase()}
              </Badge>
            )}
          </Row>
        </Stack>
        <PageHeaderRight>
          {!loading && (
            <Button variant="primary" onClick={onEditClick}>
              ✎ Edit Farm
            </Button>
          )}
        </PageHeaderRight>
      </PageHeader>

      {loading ? (
        <LoadingContainer>
          <Text variant="paragraph_medium" color="#718096">
            Loading farm details...
          </Text>
        </LoadingContainer>
      ) : (
        <FarmGrid>
          <CardRow>
            <IdentificationCard icon="User" title="Identification">
              <IdTop>
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    Farm Name
                  </Text>
                  <Text variant="paragraph_medium">{farm.name ?? '—'}</Text>
                </Stack>
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    Farm ID
                  </Text>
                  <Text variant="paragraph_medium">
                    {farm.id !== null ? String(farm.id) : '—'}
                  </Text>
                </Stack>
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    External ID (asset id)
                  </Text>
                  <Text variant="paragraph_medium">{farm.asset_id ?? '—'}</Text>
                </Stack>
              </IdTop>
              {farm.access_code !== null && (
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    Access Code
                  </Text>
                  <Text variant="paragraph_medium">{farm.access_code}</Text>
                </Stack>
              )}
              <Stack gutter="xxs">
                <Text variant="label_small_allcaps" color="#718096">
                  System UUID
                </Text>
                <UuidBox>
                  <UuidText>{farm.uuid ?? '—'}</UuidText>
                  <CopyBtn
                    onClick={copyUuid}
                    aria-label="Copy UUID"
                    title="Copy UUID"
                  >
                    <Icon icon="Copy" />
                    {copied && <CopiedText>Copied!</CopiedText>}
                  </CopyBtn>
                </UuidBox>
              </Stack>
            </IdentificationCard>

            <CapacityCard>
              <Text variant="label_small_allcaps" color="#2563eb">
                Capacity Output
              </Text>
              <CapacityValue>
                <CapacityNumber>
                  {farm.size !== null ? farm.size : '—'}
                </CapacityNumber>
                {farm.size !== null && <CapacityUnit>MW</CapacityUnit>}
              </CapacityValue>
              {farm.ac_size_megawatts !== null && (
                <Efficiency>
                  <EfficiencyRow>
                    <EfficiencyLabel>AC Capacity</EfficiencyLabel>
                    <EfficiencyPct>{farm.ac_size_megawatts} MW</EfficiencyPct>
                  </EfficiencyRow>
                  <ProgressTrack>
                    <ProgressFill
                      style={{
                        width: `${Math.min(
                          100,
                          ((farm.ac_size_megawatts ?? 0) / (farm.size ?? 1)) *
                            100,
                        )}%`,
                      }}
                    />
                  </ProgressTrack>
                </Efficiency>
              )}
            </CapacityCard>
          </CardRow>

          <LocationRow>
            <LocationCard icon="Map" title="Location Details">
              <LocationInner>
                <LocationFields>
                  <LocationFieldsRow>
                    <Stack gutter="xxs">
                      <Text variant="label_small_allcaps" color="#718096">
                        Country
                      </Text>
                      <Text variant="paragraph_medium">
                        {farm.country ?? '—'}
                      </Text>
                    </Stack>
                    <Stack gutter="xxs">
                      <Text variant="label_small_allcaps" color="#718096">
                        State
                      </Text>
                      <Text variant="paragraph_medium">
                        {farm.state ?? '—'}
                      </Text>
                    </Stack>
                  </LocationFieldsRow>
                  <Stack gutter="xxs" style={{ marginTop: '0.75rem' }}>
                    <Text variant="label_small_allcaps" color="#718096">
                      Address
                    </Text>
                    <Text variant="paragraph_medium">
                      {farm.address ?? '—'}
                    </Text>
                  </Stack>
                  <CoordsTable>
                    <tbody>
                      <tr>
                        <CoordsKey>Latitude</CoordsKey>
                        <CoordsVal>
                          {farm.latitude !== null ? farm.latitude : '—'}
                        </CoordsVal>
                      </tr>
                      <tr>
                        <CoordsKey>Longitude</CoordsKey>
                        <CoordsVal>
                          {farm.longitude !== null ? farm.longitude : '—'}
                        </CoordsVal>
                      </tr>
                    </tbody>
                  </CoordsTable>
                </LocationFields>
                <MapPlaceholder>
                  {farm.latitude !== null && farm.longitude !== null ? (
                    <MapLink
                      href={`https://www.google.com/maps?q=${farm.latitude},${farm.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon icon="LocationDot" size="large" color="#2563eb" />
                      <span>
                        {farm?.latitude?.toFixed(5)},{' '}
                        {farm?.longitude?.toFixed(5)}
                      </span>
                      <MapLinkSub>Open in Google Maps ↗</MapLinkSub>
                    </MapLink>
                  ) : (
                    <MapNoCoords>
                      <Text variant="paragraph_small" color="#718096">
                        No coordinates available
                      </Text>
                    </MapNoCoords>
                  )}
                </MapPlaceholder>
              </LocationInner>
            </LocationCard>
            <PartnerCard farm={farm} />
          </LocationRow>

          <ThreeRow>
            <FarmInfoCard icon="House" title="Organization">
              <OrgField>
                <Text variant="label_small_allcaps" color="#718096">
                  Org ID
                </Text>
                <Text variant="paragraph_medium" color="#2563eb">
                  {farm.org_id !== null ? farm.org_id : '—'}
                </Text>
              </OrgField>
              <OrgField>
                <Text variant="label_small_allcaps" color="#718096">
                  Client Org ID
                </Text>
                <Text variant="paragraph_medium" color="#2563eb">
                  {farm.client_org_id !== null ? farm.client_org_id : '—'}
                </Text>
              </OrgField>
              <OrgField>
                <Text variant="label_small_allcaps" color="#718096">
                  Creator ID
                </Text>
                <Text variant="paragraph_medium" color="#2563eb">
                  {farm.creator_id !== null ? farm.creator_id : '—'}
                </Text>
              </OrgField>
            </FarmInfoCard>

            <FarmInfoCard icon="ChartLineUpDown" title="Site Specifics">
              <Stack gutter="md">
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    PPA Rate
                  </Text>
                  <SpecValue>
                    {farm.ppa_rate !== null ? (
                      <>
                        <SpecBig>${farm?.ppa_rate?.toFixed(2)}</SpecBig>
                        <SpecUnit>/kWh</SpecUnit>
                      </>
                    ) : (
                      <SpecBig>—</SpecBig>
                    )}
                  </SpecValue>
                </Stack>
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    Peak Sunlight
                  </Text>
                  <SpecValue>
                    <SpecBig>
                      {farm.peak_sunlight_hours_annual !== null
                        ? farm.peak_sunlight_hours_annual
                        : '—'}
                    </SpecBig>
                    {farm.peak_sunlight_hours_annual !== null && (
                      <SpecUnit> hrs</SpecUnit>
                    )}
                  </SpecValue>
                </Stack>
              </Stack>
            </FarmInfoCard>

            <FarmInfoCard icon="Clock" title="System History">
              <Timeline>
                <TimelineItem>
                  <TimelineDot $color="blue" />
                  <TimelineContent>
                    <Text variant="label_small_allcaps" color="#718096">
                      Created
                    </Text>
                    <TimelineDate>
                      {formatDate(farm.created_tsecs)}
                    </TimelineDate>
                    <TimelineNote>System initialization</TimelineNote>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineDot $color="green" />
                  <TimelineContent>
                    <Text variant="label_small_allcaps" color="#718096">
                      Commissioned
                    </Text>
                    <TimelineDate>
                      {formatDate(farm.commissioning_tsecs)}
                    </TimelineDate>
                    <TimelineNote>Grid interconnection</TimelineNote>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineDot $color="gray" />
                  <TimelineContent>
                    <Text variant="label_small_allcaps" color="#718096">
                      Last Updated
                    </Text>
                    <TimelineDate>
                      {formatDate(farm.updated_tsecs)}
                    </TimelineDate>
                    <TimelineNote>Last modification</TimelineNote>
                  </TimelineContent>
                </TimelineItem>
              </Timeline>
            </FarmInfoCard>
          </ThreeRow>

          <FarmInfoCard icon="LayerGroup" title="Module Configuration">
            <ModulesRow>
              <ModuleTile>
                <ModuleIcon>
                  <Icon icon="BorderInner" size="medium" />
                </ModuleIcon>
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    Layout
                  </Text>
                  <ModuleVal>{farm.module_layout ?? '—'}</ModuleVal>
                </Stack>
              </ModuleTile>
              <ModuleTile>
                <ModuleIcon>
                  <Icon icon="Gear" size="medium" />
                </ModuleIcon>
                <Stack gutter="xxs">
                  <Text variant="label_small_allcaps" color="#718096">
                    Mounting
                  </Text>
                  <ModuleVal>
                    {farm.module_mount ? farm.module_mount.toUpperCase() : '—'}
                  </ModuleVal>
                </Stack>
              </ModuleTile>
              {farm.site_layout && (
                <ModuleTile>
                  <ModuleIcon>
                    <Icon icon="VectorSquare" size="medium" />
                  </ModuleIcon>
                  <Stack gutter="xxs">
                    <Text variant="label_small_allcaps" color="#718096">
                      Site Layout
                    </Text>
                    <ModuleVal>{farm.site_layout}</ModuleVal>
                  </Stack>
                </ModuleTile>
              )}
            </ModulesRow>
          </FarmInfoCard>
        </FarmGrid>
      )}
    </Wrapper>
  );
}
